'use client';

import { useState, useCallback, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import {
  TextField,
  CircularProgress,
  Autocomplete,
  Card,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { toast } from 'src/components/snackbar';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { _mock } from 'src/_mock';
import { varAlpha } from 'src/theme/styles';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { AnimateAvatar } from 'src/components/animate';

import { useAuthContext } from 'src/auth/hooks';

import { UpgradeBlock } from './nav-upgrade';
import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';
import { signOut as jwtSignOut } from 'src/auth/context/jwt/action';

import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import {
  useGetEmployeesStatus,
  changeEmployeeStatus,
  useGetAll,
  useUpdateEmployee,
  changePassword,
} from 'src/actions/setup/setupActions.ts';
import { Form, Field } from 'src/components/hook-form';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next'; // Import the hook
import { CONFIG } from 'src/config-global';
import FormErrorBoundary from 'src/sections/error/FormErrorBoundary';

export function AccountDrawer({ data = [], sx, ...other }) {
  const { t } = useTranslation('account'); // Make sure you have "account.json" in your locales

  const { employeeId, checkUserSession } = useAuthContext();

  const signOut =
    (CONFIG.auth.method === 'supabase' && supabaseSignOut) ||
    (CONFIG.auth.method === 'firebase' && firebaseSignOut) ||
    (CONFIG.auth.method === 'amplify' && amplifySignOut) ||
    jwtSignOut;

  const { employeeStatus, employeeStatusLoading, employeeStatusError, mutateEmployeeStatus } =
    useGetEmployeesStatus();

  const { employeeDetails, employeeLoading, mutateEmployee } = useGetAll(employeeId);

  const { updateEmployeeProfile } = useUpdateEmployee();

  const theme = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const { tairraUser } = useAuthContext();

  const [open, setOpen] = useState(false);
  const [statusdialogOpen, setStatusDialogOpen] = useState(false);

  const [profiledialogOpen, setProfileDialogOpen] = useState(false);

  const [pwdialogOpen, setPWDialogOpen] = useState(false);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickItem = useCallback(
    (path) => {
      handleCloseDrawer();
      router.push(path);
    },
    [handleCloseDrawer, router]
  );

  const handleOpenStatusDialog = useCallback(() => {
    setStatusDialogOpen(true);
    handleCloseDrawer();

    if (employeeDetails?.status) {
      const status = employeeDetails.status;
      setFormData({
        description: status.description || '',
        employeeId: status.employeeId || employeeId,
        employeeStatusId: status.employeeStatusId || '',
        isActive: status.isActive || true,
        name: status.name || '',
        workStartTime: status.workStartTime || '',
        workEndTime: status.workEndTime || '',
      });
    } else {
      setFormData({
        description: '',
        employeeId: employeeId,
        employeeStatusId: '',
        isActive: true,
        name: '',
        workStartTime: '',
        workEndTime: '',
      });
    }
  }, [employeeDetails, employeeId]);

  const handleCloseStatusDialog = useCallback(() => {
    setStatusDialogOpen(false);
    setFormData({
      description: '',
      employeeId: employeeId,
      employeeStatusId: '',
      isActive: true,
      name: '',
      workStartTime: '',
      workEndTime: '',
    });
  }, []);

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    description: '',
    employeeId: employeeId,
    employeeStatusId: '',
    isActive: true,
    name: '',
    workStartTime: '',
    workEndTime: '',
  });

  const [showStatusError, setShowStatusError] = useState(false);

  const handleworkStartTimeChange = (date) => {
    if (!date) {
      setShowStatusError(true);
      setFormData((prevData) => ({
        ...prevData,
        workStartTime: null,
      }));
      return;
    }

    setShowStatusError(false);

    const formattedDate = dayjs(date).format('YYYY-MM-DDTHH:mm:ss.sssZ');

    if (dayjs(formattedDate).isAfter(dayjs(formData.workEndTime))) {
      toast.error('Start date cannot be after the end date.');
      setFormData((prevData) => ({
        ...prevData,
        workStartTime: null,
      }));
      setShowStatusError(true);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        workStartTime: formattedDate,
      }));
    }
  };

  const handleworkEndTimeChange = (date) => {
    if (!date) {
      setShowStatusError(true);
      toast.error('End Date is required');
      setFormData((prevData) => ({
        ...prevData,
        workEndTime: null,
      }));
      return;
    }

    setShowStatusError(false);
    const formattedDate = dayjs(date).format('YYYY-MM-DDTHH:mm:ss.sssZ');

    if (dayjs(formattedDate).isBefore(dayjs(formData.workStartTime))) {
      toast.error('End date cannot be before the start date.');
      setFormData((prevData) => ({
        ...prevData,
        workEndTime: null,
      }));
      setShowStatusError(true);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        workEndTime: formattedDate,
      }));
    }
  };

  const handleStatusChange = (event, newValue) => {
    setFormData((prevData) => ({
      ...prevData,
      employeeStatusId: newValue ? newValue.id : '',
      name: newValue ? newValue.name : '',
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeStatus = async () => {
    if (!formData.workStartTime || !formData.workEndTime) {
      setShowStatusError(true);
      return;
    }

    setLoading(true);

    const statusData = {
      ...formData,
    };

    console.log('Status Data:', statusData);

    const response = await changeEmployeeStatus(statusData);

    if (response.success) {
      toast.success('Status Updated Successfully');
      handleCloseStatusDialog();

      await mutateEmployee();

      setFormData({
        description: '',
        employeeId: employeeId,
        employeeStatusId: '',
        isActive: true,
        name: '',
        workStartTime: '',
        workEndTime: '',
      });
    } else {
      toast.error('Failed to update status');
    }

    setLoading(false);
  };

  const methods = useForm({
    mode: 'onSubmit',
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [firstName, setfirstName] = useState('');
  const [email, setEmail] = useState('');
  const [employeeStaffId, setemployeeStaffId] = useState('');

  const [showError, setShowError] = useState(false);

  const handleOpenProfileDialog = useCallback(() => {
    setProfileDialogOpen(true);
    handleCloseDrawer();
    if (employeeDetails) {
      setProfileFormData({
        clientId: employeeDetails?.clientId || '',
        contactNumber1: employeeDetails?.employeeDetails || '',
        email: employeeDetails?.email || '',
        employeeStaffId: employeeDetails?.employeeStaffId || '',
        fbLink: employeeDetails?.fbLink || '',
        firstName: employeeDetails?.firstName || '',
        gender: employeeDetails?.gender || '',
        instaLink: employeeDetails?.instaLink || '',
        linkedInLink: employeeDetails?.linkedInLink || '',
        nationalIDNumber: employeeDetails?.nationalIDNumber || '',
        photoId: employeeDetails?.photoId || '',
        photoPath: employeeDetails?.photoPath || '',
        twitterLink: employeeDetails?.twitterLink || '',
        whatsappLink: employeeDetails?.whatsappLink || '',
      });
    } else {
      setProfileFormData({
        clientId: '',
        contactNumber1: '',
        email: '',
        employeeStaffId: '',
        fbLink: '',
        firstName: '',
        gender: '',
        instaLink: '',
        linkedInLink: '',
        nationalIDNumber: '',
        photoId: '',
        photoPath: '',
        twitterLink: '',
        whatsappLink: '',
      });
    }
  }, [employeeDetails]);

  const handleCloseProfileDialog = useCallback(() => {
    setProfileDialogOpen(false);
    setfirstName(''), setEmail(''), setemployeeStaffId(''), setShowError(false);
  }, []);

  const [profileformData, setProfileFormData] = useState({
    clientId: '',
    contactNumber1: '',
    email: '',
    employeeStaffId: '',
    fbLink: '',
    firstName: '',
    gender: '',
    instaLink: '',
    linkedInLink: '',
    nationalIDNumber: '',
    photoId: '',
    photoPath: '',
    twitterLink: '',
    whatsappLink: '',
  });

  const handleFileUpload = ({ fileId, path }) => {
    // console.log('File uploaded:', { fileId, path });

    setProfileFormData((prevData) => ({
      ...prevData,
      photoPath: path,
      photoId: fileId,
    }));
  };

  const onSubmit = handleSubmit(async () => {
    if (!profileformData.firstName || !profileformData.email || !profileformData.employeeStaffId) {
      setShowError(true);
      return;
    }

    const payload = {
      ...profileformData,
    };

    console.log('This is the payload for change profile', payload);
    const response = await updateEmployeeProfile(employeeDetails?.id, payload);
    await mutateEmployee();

    handleCloseProfileDialog();

    // if (response.success) {
    //   handleCloseProfileDialog();
    //   await mutateEmployee();
    // } else {
    //   handleCloseProfileDialog();
    // }
  });

  const handleOpenPWDialog = useCallback(() => {
    setPWDialogOpen(true);
    handleCloseDrawer();
  });

  const handleClosePWDialog = useCallback(() => {
    setPWDialogOpen(false);
    setShowError(false);
    setPasswordMatchError(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setPWFormData({
      currentPassword: '',
      newpw: '',
      updatedPassword: '',
    });
    setPasswordStrength({
      length: false,
      number: false,
      uppercase: false,
      specialChar: false,
    });
  });

  const [pwformData, setPWFormData] = useState({
    currentPassword: '',
    newpw: '',
    updatedPassword: '',
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    number: false,
    uppercase: false,
    specialChar: false,
  });

  const [passwordMatchError, setPasswordMatchError] = useState(false);

  const handleClickShowCurrentPassword = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;

    const lengthValid = value.length >= 8 && value.length <= 22;
    const numberValid = /[0-9]/.test(value);
    const uppercaseValid = /[A-Z]/.test(value);
    const specialCharValid = /[!@#$%^&*()_+{}\[\]:;"'<>,.?\/\\|`~]/.test(value);

    setPWFormData({
      ...pwformData,
      newpw: value,
    });

    setPasswordStrength({
      length: lengthValid,
      number: numberValid,
      uppercase: uppercaseValid,
      specialChar: specialCharValid,
    });

    setShowError(false);
    setPasswordMatchError(pwformData.updatedPassword && value !== pwformData.updatedPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setPWFormData({
      ...pwformData,
      updatedPassword: value,
    });

    setPasswordMatchError(pwformData.newpw && value !== pwformData.newpw);
    setShowError(false);
  };

  const onSubmitPassword = handleSubmit(async () => {
    if (!pwformData.currentPassword || !pwformData.newpw || !pwformData.updatedPassword) {
      setShowError(true);
      return;
    }

    const { length, number, uppercase, specialChar } = passwordStrength;

    if (!length || !number || !uppercase || !specialChar) {
      setShowError(true);
      return;
    }

    const { newpw, ...payload } = pwformData;

    const response = await changePassword(payload);

    console.log('response', response);
    if (response?.success) {
      handleClosePWDialog();

      setPWFormData({
        currentPassword: '',
        newpw: '',
        updatedPassword: '',
      });

      setPasswordStrength({
        length: false,
        number: false,
        uppercase: false,
        specialChar: false,
      });
      await signOut();
      await checkUserSession?.();
      router.refresh();
    }
  });

  const renderAvatar = (
    <AnimateAvatar
      width={96}
      slotProps={{
        avatar: { src: employeeDetails?.photoPath, alt: employeeDetails?.userName },
        overlay: {
          border: 2,
          spacing: 3,
          color: `linear-gradient(135deg, ${varAlpha(theme.vars.palette.primary.mainChannel, 0)} 25%, ${theme.vars.palette.primary.main} 100%)`,
        },
      }}
    >
      {employeeDetails?.firstName?.charAt(0).toUpperCase()}
    </AnimateAvatar>
  );

  return (
    <>
      <AccountButton
        onClick={handleOpenDrawer}
        photoURL={employeeDetails?.photoPath}
        displayName={employeeDetails?.firstName}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={handleCloseDrawer}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 320 } }}
      >
        <IconButton
          onClick={handleCloseDrawer}
          sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar>
          <Stack alignItems="center" sx={{ pt: 8, pb: 4 }}>
            {renderAvatar}

            <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
              {employeeDetails?.firstName}
            </Typography>

            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {employeeDetails?.designation?.name}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {employeeDetails?.email}
            </Typography>

            <Label color="warning" sx={{ mt: 1 }}>
              {employeeDetails?.status?.name || 'On Duty'}
            </Label>
          </Stack>

          <Stack
            sx={{
              py: 3,
              px: 2.5,
              borderTop: `dashed 1px ${theme.vars.palette.divider}`,
              borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
            }}
          >
            {data.map((option) => {
              const rootLabel = pathname.includes('/dashboard') ? 'Home' : 'Dashboard';
              const rootHref = pathname.includes('/dashboard') ? '/' : paths.dashboard.root;

              return (
                <MenuItem
                  key={option.labelKey}
                  onClick={() => {
                    const labelKey = option.labelKey;
                    if (labelKey === 'changeStatus') {
                      handleOpenStatusDialog();
                    } else if (labelKey === 'accountSettings') {
                      handleOpenProfileDialog();
                    } else if (labelKey === 'changePassword') {
                      handleOpenPWDialog();
                    } else {
                      handleClickItem(labelKey === 'home' ? rootHref : option.href);
                    }
                  }}
                  sx={{
                    py: 1,
                    color: 'text.secondary',
                    '& svg': { width: 24, height: 24 },
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {option.icon}

                  <Box component="span" sx={{ ml: 2 }}>
                    {t(option.labelKey)} {/* Translate using labelKey */}
                  </Box>

                  {option.info && (
                    <Label color="error" sx={{ ml: 1 }}>
                      {option.info}
                    </Label>
                  )}
                </MenuItem>
              );
            })}
          </Stack>
        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <SignOutButton onClose={handleCloseDrawer} />
        </Box>
      </Drawer>

      <Dialog open={statusdialogOpen} onClose={handleCloseStatusDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ ml: 2.5 }}>{t('changeStatus')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ p: 3 }}>
            <Autocomplete
              options={employeeStatus || []}
              getOptionLabel={(option) => option?.name || ''}
              onChange={handleStatusChange}
              value={
                employeeStatus?.find((status) => status.id === formData.employeeStatusId) || null
              }
              renderInput={(params) => (
                <TextField {...params} label={t('status')} variant="outlined" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              noOptionsText="No status found"
            />

            <TextField
              fullWidth
              label={t('description')}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
            />

            <DateTimePicker
              label={t('startDate')}
              value={formData.workStartTime ? dayjs(formData.workStartTime) : null}
              onChange={handleworkStartTimeChange}
              name="workStartTime"
              renderInput={(params) => <TextField {...params} />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={showStatusError && !formData.workStartTime}
                  helperText={
                    showStatusError && !formData.workStartTime ? 'Start Date is required' : ''
                  }
                />
              )}
            />

            <DateTimePicker
              label={t('endDate')}
              value={formData.workEndTime ? dayjs(formData.workEndTime) : null}
              onChange={handleworkEndTimeChange}
              name="workEndTime"
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={showStatusError && !formData.workEndTime}
                  helperText={
                    showStatusError && !formData.workEndTime ? 'End Date is required' : ''
                  }
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog} variant="contained">
            {t('close')}
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeStatus}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {t('update')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={profiledialogOpen} onClose={handleCloseProfileDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ ml: 2.5 }}> {t('updateProfile')}</DialogTitle>
        <DialogContent>
          <FormErrorBoundary>
            <Form methods={methods} onSubmit={onSubmit}>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={12}>
                    <Field.UploadPhoto
                      name="photoPath"
                      value={profileformData.photoPath}
                      onFileUpload={handleFileUpload}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          photoPath: e.target.value,
                        });
                      }}
                      maxSize={3145728}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('employeeId')}
                      name="employeeStaffId"
                      value={profileformData.employeeStaffId}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          employeeStaffId: e.target.value,
                        });
                        setShowError(false);
                      }}
                      required
                      disabled
                      error={showError && !profileformData.employeeStaffId}
                      helperText={
                        showError && !profileformData.employeeStaffId
                          ? 'Employee Id is Required'
                          : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('fullName')}
                      name="firstName"
                      value={profileformData.firstName}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          firstName: e.target.value,
                        });
                        setShowError(false);
                      }}
                      required
                      error={showError && !profileformData.firstName}
                      helperText={showError && !profileformData.firstName ? 'Name is Required' : ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('email')}
                      name="email"
                      value={profileformData.email}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          email: e.target.value,
                        });
                        setShowError(false);
                      }}
                      disabled
                      required
                      error={showError && !profileformData.email}
                      helperText={showError && !profileformData.email ? 'Email is Required' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('idNumber')}
                      name="nationalIDNumber"
                      value={profileformData.nationalIDNumber}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          nationalIDNumber: e.target.value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('linkedIn')}
                      name="linkedInLink"
                      value={profileformData.linkedInLink}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          linkedInLink: e.target.value,
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('facebook')}
                      name="fbLink"
                      value={profileformData.fbLink}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          fbLink: e.target.value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('whatsapp')}
                      name="whatsappLink"
                      value={profileformData.whatsappLink}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          whatsappLink: e.target.value,
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('instagram')}
                      name="instaLink"
                      value={profileformData.instaLink}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          instaLink: e.target.value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('twitter')}
                      name="twitterLink"
                      value={profileformData.twitterLink}
                      onChange={(e) => {
                        setProfileFormData({
                          ...profileformData,
                          twitterLink: e.target.value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        name="gender"
                        value={profileformData.gender}
                        onChange={(e) => {
                          setProfileFormData({
                            ...profileformData,
                            gender: e.target.value,
                          });
                        }}
                      >
                        <FormControlLabel value="1" control={<Radio />} label={t('male')} />
                        <FormControlLabel value="2" control={<Radio />} label={t('female')} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Stack>
            </Form>
          </FormErrorBoundary>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfileDialog} variant="contained">
            {t('close')}
          </Button>
          <Button variant="contained" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('updating') : t('update')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pwdialogOpen} onClose={handleClosePWDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ ml: 2.5 }}>{t('changePassword')}</DialogTitle>
        <DialogContent>
          <FormErrorBoundary>
            <Form methods={methods} onSubmit={onSubmitPassword}>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      fullWidth
                      label={t('currentPassword')}
                      name="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={pwformData.currentPassword}
                      onChange={(e) => {
                        setPWFormData({
                          ...pwformData,
                          currentPassword: e.target.value,
                        });
                        setShowError(false);
                      }}
                      required
                      error={showError && !pwformData.currentPassword}
                      helperText={
                        showError && !pwformData.currentPassword ? t('enterCurrentPassword') : ''
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowCurrentPassword}
                              edge="end"
                            >
                              <Iconify
                                icon={showCurrentPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                        inputProps: {
                          style: {
                            // Hides Edge-specific password reveal button
                            '::-ms-reveal': { display: 'none' },
                            '::-ms-clear': { display: 'none' },
                          },
                        },
                      }}
                      sx={{
                        '& input::-ms-reveal': {
                          display: 'none',
                        },
                        '& input::-ms-clear': {
                          display: 'none',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <TextField
                      fullWidth
                      label={t('newPassword')}
                      name="newpw"
                      type={showNewPassword ? 'text' : 'password'}
                      value={pwformData.newpw}
                      onChange={handlePasswordChange}
                      required
                      error={showError && !pwformData.newpw}
                      helperText={showError && !pwformData.newpw ? t('enterNewPassword') : ''}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowNewPassword}
                              edge="end"
                            >
                              <Iconify
                                icon={showNewPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                        inputProps: {
                          style: {
                            // Hides Edge-specific password reveal button
                            '::-ms-reveal': { display: 'none' },
                            '::-ms-clear': { display: 'none' },
                          },
                        },
                      }}
                      sx={{
                        '& input::-ms-reveal': {
                          display: 'none',
                        },
                        '& input::-ms-clear': {
                          display: 'none',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {t('passwordRequirements')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1 }}
                      color={
                        passwordStrength.length &&
                        passwordStrength.number &&
                        passwordStrength.uppercase &&
                        passwordStrength.specialChar
                          ? 'primary'
                          : 'error'
                      }
                    >
                      {passwordStrength.length ? (
                        <Iconify
                          icon="solar:check-circle-bold"
                          width={16}
                          sx={{ color: 'success.main', mr: 1 }}
                        />
                      ) : (
                        <Iconify
                          icon="mdi:close-circle"
                          width={16}
                          sx={{ color: 'error.main', mr: 1 }}
                        />
                      )}
                      {t('passwordLength')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={passwordStrength.number ? 'primary' : 'error'}
                    >
                      {passwordStrength.number ? (
                        <Iconify
                          icon="solar:check-circle-bold"
                          width={16}
                          sx={{ color: 'success.main', mr: 1 }}
                        />
                      ) : (
                        <Iconify
                          icon="mdi:close-circle"
                          width={16}
                          sx={{ color: 'error.main', mr: 1 }}
                        />
                      )}
                      {t('numericDigit')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={passwordStrength.uppercase ? 'primary' : 'error'}
                    >
                      {passwordStrength.uppercase ? (
                        <Iconify
                          icon="solar:check-circle-bold"
                          width={16}
                          sx={{ color: 'success.main', mr: 1 }}
                        />
                      ) : (
                        <Iconify
                          icon="mdi:close-circle"
                          width={16}
                          sx={{ color: 'error.main', mr: 1 }}
                        />
                      )}
                      {t('uppercaseLetter')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={passwordStrength.specialChar ? 'primary' : 'error'}
                    >
                      {passwordStrength.specialChar ? (
                        <Iconify
                          icon="solar:check-circle-bold"
                          width={16}
                          sx={{ color: 'success.main', mr: 1 }}
                        />
                      ) : (
                        <Iconify
                          icon="mdi:close-circle"
                          width={16}
                          sx={{ color: 'error.main', mr: 1 }}
                        />
                      )}
                      {t('specialCharacter')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <TextField
                      fullWidth
                      label={t('confirmPassword')}
                      name="updatedPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={pwformData.updatedPassword}
                      onChange={handleConfirmPasswordChange}
                      required
                      error={passwordMatchError || (showError && !pwformData.updatedPassword)}
                      helperText={
                        passwordMatchError
                          ? 'Passwords do not match'
                          : showError && !pwformData.updatedPassword
                            ? t('enterConfirmPassword')
                            : ''
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowConfirmPassword}
                              edge="end"
                            >
                              <Iconify
                                icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                        inputProps: {
                          style: {
                            // Hides Edge-specific password reveal button
                            '::-ms-reveal': { display: 'none' },
                            '::-ms-clear': { display: 'none' },
                          },
                        },
                      }}
                      sx={{
                        '& input::-ms-reveal': {
                          display: 'none',
                        },
                        '& input::-ms-clear': {
                          display: 'none',
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Form>
          </FormErrorBoundary>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePWDialog} variant="contained">
            {t('close')}
          </Button>
          <Button
            variant="contained"
            onClick={onSubmitPassword}
            disabled={isSubmitting}
            sx={{
              bgcolor:
                !pwformData.currentPassword ||
                !pwformData.newpw ||
                !pwformData.updatedPassword ||
                isSubmitting ||
                Object.values(passwordStrength).includes(false) ||
                passwordMatchError
                  ? 'grey.400'
                  : '',
            }}
          >
            {isSubmitting ? t('updating') : t('updatePassword')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
